import axios from 'axios';
import 'datatables.net';
import 'datatables.net-buttons';
import 'datatables.net-buttons/js/buttons.colVis';
import 'datatables.net-buttons/js/buttons.html5';
import 'datatables.net-buttons/js/buttons.print';
import $ from 'jquery';
import Swal from 'sweetalert2';
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { baseurl } from '../utility/helper';

// ─────────────────────────────────────────────
// Filter options
// ─────────────────────────────────────────────
const fieldOptions = [
  // { value: 'chapter',              label: 'Chapter'                        },
  { value: 'join_date',            label: 'Join Date'                      },
  { value: 'failed',               label: 'Failed'                         },
  { value: 'passed',               label: 'Passed'                         },
  { value: 'passed_not_joined',    label: 'Passed but Not Joined'          },
  { value: 'passed_and_joined',    label: 'Passed and Joined'              },
  { value: 'prior_evidence',       label: 'Prior Evidence'                 },
];

// Filters that need a date-range picker when selected
const DATE_RANGE_FILTERS = ['join_date', 'failed', 'passed', 'passed_not_joined', 'passed_and_joined', 'prior_evidence'];

// Filters that are mutually exclusive with each other (only one at a time)
const exclusiveFilters = ['join_date', 'failed', 'passed', 'passed_not_joined', 'passed_and_joined', 'prior_evidence'];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const formatDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateShow = (data) => {
  if (!data) return '';
  const d = new Date(data);
  const day   = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
const CandidateReports = () => {
  const roleid        = parseInt(sessionStorage.getItem('userroleId'));
  const initialChapter = roleid === 2 ? sessionStorage.getItem('userchapterId') || '' : '';

  const [loading,      setLoading]      = useState(false);
  const [chapters,     setChapters]     = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showTable,    setShowTable]    = useState(false);
  const [noData,       setNoData]       = useState(false);
  const [hasSearched,  setHasSearched]  = useState(false);

  const [filters, setFilters] = useState({
    selectedFields: [],
    chapterId:      '',
    startDate:      null,
    endDate:        null,
    // individual date buckets sent to API
    joinDateStart:          null,
    joinDateEnd:            null,
    failedDateStart:        null,
    failedDateEnd:          null,
    passedDateStart:        null,
    passedDateEnd:          null,
    passedNotJoinedStart:   null,
    passedNotJoinedEnd:     null,
    passedAndJoinedStart:   null,
    passedAndJoinedEnd:     null,
    priorEvidenceStart:     null,
    priorEvidenceEnd:       null,
  });

  const tableRef = useRef(null);

  // ── Fetch dropdown data ──
  useEffect(() => {
    const fetchChapters = async () => {
      try {
        const res = await axios.get(`${baseurl}/admin/chapters`);
        setChapters(res.data);
      } catch (err) {
        console.error('Error fetching chapters:', err);
      }
    };
    fetchChapters();
  }, []);

  // ── Fetch report data ──
  const fetchData = async (f) => {
    try {
      if (roleid === 2 && !initialChapter) {
        Swal.fire({ icon: 'warning', title: 'Chapter Missing', text: 'Please select a chapter before fetching data.' });
        return [];
      }

      const q = new URLSearchParams();

      if (f.chapterId)             q.append('chapter_id',              f.chapterId);
      if (f.joinDateStart)         q.append('joinDateStart',           formatDate(f.joinDateStart));
      if (f.joinDateEnd)           q.append('joinDateEnd',             formatDate(f.joinDateEnd));
      if (f.failedDateStart)       q.append('failedDateStart',         formatDate(f.failedDateStart));
      if (f.failedDateEnd)         q.append('failedDateEnd',           formatDate(f.failedDateEnd));
      if (f.passedDateStart)       q.append('passedDateStart',         formatDate(f.passedDateStart));
      if (f.passedDateEnd)         q.append('passedDateEnd',           formatDate(f.passedDateEnd));
      if (f.passedNotJoinedStart)  q.append('passedNotJoinedStart',    formatDate(f.passedNotJoinedStart));
      if (f.passedNotJoinedEnd)    q.append('passedNotJoinedEnd',      formatDate(f.passedNotJoinedEnd));
      if (f.passedAndJoinedStart)  q.append('passedAndJoinedStart',    formatDate(f.passedAndJoinedStart));
      if (f.passedAndJoinedEnd)    q.append('passedAndJoinedEnd',      formatDate(f.passedAndJoinedEnd));
      if (f.priorEvidenceStart)    q.append('priorEvidenceStart',      formatDate(f.priorEvidenceStart));
      if (f.priorEvidenceEnd)      q.append('priorEvidenceEnd',        formatDate(f.priorEvidenceEnd));
      if (f.selectedFields)        q.append('selectedValues',            f.selectedFields);
      
      // Boolean flags for filters that don't need dates (none currently, but kept for extensibility)
      if (f.selectedFields.includes('prior_evidence')) q.append('prior_evidence', 1);

      const res = await axios.get(`${baseurl}/admin/candidate/filtered?${q.toString()}`);
      return res.data;
    } catch (err) {
      console.error('Error fetching candidate data:', err);
      return [];
    }
  };

  // ── DataTable ──
  useEffect(() => {
    if (!hasSearched || !showTable) return;

    const needsPassedNotJoined = filters.selectedFields.includes('passed_not_joined');

    $(tableRef.current).DataTable({
      destroy:    true,
      dom:        'Blfrtip',
      pageLength: 25,
      lengthMenu: [[10, 25, 50, 100, 250, 500, -1], [10, 25, 50, 100, 250, 500, 'All']],
      buttons: [
        { extend: 'colvis',    className: 'btn btn-secondary dttable', columns: ':visible' },
        { extend: 'copyHtml5', className: 'btn btn-secondary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'excelHtml5',className: 'btn btn-secondary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'csvHtml5',  className: 'btn btn-secondary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'pdfHtml5',  className: 'btn btn-secondary dttable', exportOptions: { columns: ':visible' } },
        { extend: 'print',     className: 'btn btn-secondary dttable', exportOptions: { columns: ':visible' } },
      ],
      data: filteredData,
      columns: [
        {
          data: null, title: 'Sr.No', className: 'text-center',
          render: (_d, _t, _r, meta) => meta.row + 1,
        },
        { title: 'Candidate ID',    data: 'test_candidate_id',
          render: (data, type) =>
            type !== 'display'
              ? (data ?? '')
              : `<a href="/admin/CandiAdd/${data}" onclick="event.preventDefault(); window.open(this.href, '_blank');">${data ?? ''}</a>`,
        },
        {
          title: 'Name', data: null,
          render: row => `${row.first_name || ''} ${row.last_name || ''}`.trim(),
        },
        { title: 'Mobile',          data: 'mobile_no'      },
        { title: 'Email',           data: 'email'          },
        { title: 'Address',         data: 'address'},
        { title: 'City',            data: 'city'   },
        { title: 'State',           data: 'state'  },
        { title: 'Gender',          data: 'gender'         },
        {
          title: 'DOB', data: 'dob',
          render: (data, type) => type === 'display' ? formatDateShow(data) : data,
        },
        {
          title: 'Date of Registration', data: 'joining_date',
          render: (data, type) => type === 'display' ? formatDateShow(data) : data,
        },
        {
          title: 'Prior Evidence Date',
          data: 'prior_exam_date_time',
          render: (data, type) => {
            if (!data) return '-';
            return type === 'display' ? formatDateShow(data) : data;
          }
        },
        {
          title: 'Prior Evidence',
          data: 'prior_exam_result',
          render: (data) => data || '-'
        },

        // "Send Mail" column — visible only when "Passed but Not Joined" filter is active
        {
          title: needsPassedNotJoined
            ? `<input type="checkbox" id="select-all-candidate" /> Send Mail`
            : 'Send Mail',

          data: null,
          orderable: false,

          render: (_d, _t, row) => {
            const canSee =
              roleid === 1 || String(initialChapter) === String(row.chapter_id);

            // If condition TRUE → show checkbox
            if (needsPassedNotJoined && canSee) {
              return `<input type="checkbox" class="candidate-checkbox" data-id="${row.test_candidate_id}" data-email="${row.email || ''}" />`;
            }

            // If FALSE → show empty or dash
            return '-'; // or '' if you want blank
          }
        }
      ],
      initComplete: () => {
        $('.dt-length select').addClass('form-select form-select-sm');
        $('.dt-buttons .dttable').css('z-index', 0);
      },
    });

    // Select-all toggle
    $(tableRef.current).on('change', '#select-all-candidate', function () {
      $('.candidate-checkbox').prop('checked', $(this).is(':checked'));
    });

    return () => {
      if ($.fn.DataTable.isDataTable(tableRef.current)) {
        $(tableRef.current).DataTable().destroy();
      }
    };
  }, [hasSearched, showTable, filteredData]);

  // ── Field selection handler ──
  const handleFieldSelection = (selectedOptions) => {
    const selectedValues = selectedOptions.map(o => o.value);

    setFilters({
      selectedFields:          selectedValues,
      chapterId:               selectedValues.includes('chapter') ? filters.chapterId : '',
      startDate:               null,
      endDate:                 null,
      joinDateStart:           null,
      joinDateEnd:             null,
      failedDateStart:         null,
      failedDateEnd:           null,
      passedDateStart:         null,
      passedDateEnd:           null,
      passedNotJoinedStart:    null,
      passedNotJoinedEnd:      null,
      passedAndJoinedStart:    null,
      passedAndJoinedEnd:      null,
      priorEvidenceStart:      null,
      priorEvidenceEnd:        null,
    });
  };

  // ── Date change handler — maps startDate/endDate to the correct bucket ──
  const handleDateChange = (date, type) => {
    const sf = filters.selectedFields;
    const isStart = type === 'startDate';

    const patch = { [type]: date };

    if (sf.includes('join_date')) {
      if (isStart) patch.joinDateStart = date; else patch.joinDateEnd = date;
    }
    if (sf.includes('failed')) {
      if (isStart) patch.failedDateStart = date; else patch.failedDateEnd = date;
    }
    if (sf.includes('passed')) {
      if (isStart) patch.passedDateStart = date; else patch.passedDateEnd = date;
    }
    if (sf.includes('passed_not_joined')) {
      if (isStart) patch.passedNotJoinedStart = date; else patch.passedNotJoinedEnd = date;
    }
    if (sf.includes('passed_and_joined')) {
      if (isStart) patch.passedAndJoinedStart = date; else patch.passedAndJoinedEnd = date;
    }
    if (sf.includes('prior_evidence')) {
      if (isStart) patch.priorEvidenceStart = date; else patch.priorEvidenceEnd = date;
    }

    setFilters(prev => ({ ...prev, ...patch }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // ── Search ──
  const handleSearch = async () => {
    setLoading(true);

    // Show loading modal while fetching data
    Swal.fire({
      title: 'Searching...',
      text: 'Please wait while we fetch the report data.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const data = await fetchData(filters);
      setFilteredData(data);
      setNoData(data.length === 0);
      setShowTable(data.length > 0);
      setHasSearched(true);
    } finally {
      setLoading(false);
      Swal.close();
    }
  };

  // ── Bulk Send Mail (Passed but Not Joined) ──
  const handleBulkSendMail = async () => {
    const selectedIds = [];
    $('.candidate-checkbox:checked').each(function () {
      selectedIds.push($(this).data('id'));
    });

    if (selectedIds.length === 0) {
      Swal.fire('No Selection', 'Please select at least one candidate.', 'warning');
      return;
    }

    const confirm = await Swal.fire({
      title: 'Send Joining Reminder Mails?',
      text: `Send reminder mails to ${selectedIds.length} candidate(s)?`,
      icon: 'question',
      showCancelButton:   true,
      confirmButtonText:  'Yes, Send',
      cancelButtonText:   'No',
    });

    if (confirm.isConfirmed) {
      try {
        Swal.fire({
          title: 'Sending...',
          text: 'Please wait while mails are being sent.',
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });

        const response = await axios.post(`${baseurl}/admin/candidates/send_joining_mail`, {
          ids: selectedIds,
        });

        Swal.fire('Success', response.data.message || 'Mails sent successfully!', 'success');
      } catch (err) {
        console.error('Error sending mails:', err);
        Swal.fire('Error', err.response?.data?.message || 'Failed to send mails.', 'error');
      }
    }
  };

  // ── Derived state ──
  const showDateRange = filters.selectedFields.some(v => DATE_RANGE_FILTERS.includes(v));
  const showSendMail  = showTable && filters.selectedFields.includes('passed_not_joined');

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <div className="container mt-4">
      <div className="card p-4 shadow-sm">
        <h2 className="text-center mb-4">Candidate Report</h2>

        {/* ── Filters ── */}
        <div className="row g-3">

          {/* Field selector */}
          <div className="col-md-12">
            <label className="form-label">Select Filter:</label>
            <Select
              isMulti
              options={fieldOptions}
              value={fieldOptions.filter(opt => filters.selectedFields.includes(opt.value))}
              onChange={handleFieldSelection}
              className="basic-multi-select"
              classNamePrefix="select"
              isOptionDisabled={(option) => {
                // Only one exclusive filter allowed at a time
                const alreadySelected = filters.selectedFields.find(v => exclusiveFilters.includes(v));
                return (
                  alreadySelected &&
                  exclusiveFilters.includes(option.value) &&
                  !filters.selectedFields.includes(option.value)
                );
              }}
            />
          </div>

          {/* Chapter dropdown */}
          {filters.selectedFields.includes('chapter') && (
            <div className="col-md-4">
              <label className="form-label">Chapter:</label>
              <select
                className="form-select"
                name="chapterId"
                value={filters.chapterId}
                onChange={handleFilterChange}
              >
                <option value="" disabled>-- Select Chapter --</option>
                {chapters
                  .filter(c => c.chapter_id !== 1)
                  .map(c => (
                    <option key={c.chapter_id} value={c.chapter_id}>
                      {c.chapter_name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Date range pickers */}
          {showDateRange && (
            <>
              <div className="col-md-4">
                <label className="form-label">Start Date:</label>
                <DatePicker
                  selected={filters.startDate}
                  onChange={(date) => handleDateChange(date, 'startDate')}
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select start date"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">End Date:</label>
                <DatePicker
                  selected={filters.endDate}
                  onChange={(date) => handleDateChange(date, 'endDate')}
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select end date"
                />
              </div>
            </>
          )}

          {/* Search button */}
          <div className="col-12 text-center">
            <button
              className="mt-3"
              style={{ borderRadius: '8px' }}
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>

        <hr />

        {/* ── Results ── */}
        {!hasSearched ? (
          <h3 className="text-center text-muted">Please perform a search to view data.</h3>
        ) : noData ? (
          <h3 className="text-center text-danger">No Data Available</h3>
        ) : (
          <div>
            {/* Send Mail button — only for "Passed but Not Joined" */}
            {showSendMail && (
              <button
                className="btn btn-primary mb-3"
                style={{ zIndex: '0' }}
                onClick={handleBulkSendMail}
              >
                Send Joining Reminder Mails
              </button>
            )}

            <div className="table-responsive">
              <table
                id="candidate-reports"
                className="table table-striped table-bordered"
                ref={tableRef}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateReports;